import {
  GRID_VIEW_NAME_MAX_LENGTH,
  isDuplicateGridViewNameError
} from "@appretio/api";
import { Dialog, InputField, Label, Switch } from "@appretio/ui-kit";
import { useState } from "react";
import {
  formContainer,
  formErrorText,
  formField,
  toggleList
} from "./GridViewDialog.css";

export interface GridViewDialogToggle<K extends string> {
  /** Key the toggle's value is reported under. */
  key: K;
  /** Switch label. */
  label: string;
}

export interface GridViewDialogProps<K extends string> {
  /** Whether the dialog is open. */
  open: boolean;
  /** Setter for the dialog's open state. */
  onOpenChange: (open: boolean) => void;
  /** Dialog title. */
  title: string;
  /** Name the field opens with; omit for a new view. */
  initialName?: string;
  /** Placeholder of the empty name field. */
  namePlaceholder?: string;
  /** Names the user's other views hold — a duplicate is refused before the request. */
  existingNames: readonly string[];
  /** The switches under the name, in order. */
  toggles: readonly GridViewDialogToggle<K>[];
  /** Switch state the dialog opens with. */
  initialToggles: Record<K, boolean>;
  /** Keys of which at least one must stay on; the confirm refuses otherwise. */
  requireOneOf?: readonly K[];
  /** Whether the write is in flight. */
  isPending: boolean;
  /**
   * Persists the view. Resolves falsy when nothing was saved. A thrown
   * duplicate-name error lands on the name field, any other on the form.
   */
  onConfirm: (name: string, toggles: Record<K, boolean>) => Promise<unknown>;
}

/**
 * The name-and-toggles form every saved-view write uses: a new tradex or
 * temporal-grid view, a rename, a refresh. The dialog owns validation and
 * the inline errors; the caller owns what the toggles mean.
 */
export function GridViewDialog<K extends string>({
  open,
  onOpenChange,
  title,
  initialName = "",
  namePlaceholder = "View name",
  existingNames,
  toggles,
  initialToggles,
  requireOneOf,
  isPending,
  onConfirm
}: GridViewDialogProps<K>) {
  const [name, setName] = useState(initialName);
  const [values, setValues] = useState(initialToggles);
  const [errors, setErrors] = useState<{ name?: string; form?: string }>({});

  // Reset the form on each re-open — during render, so the first open frame
  // already shows the fresh form and a re-rendering parent can't reset it
  // while the user types.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(initialName);
      setValues(initialToggles);
      setErrors({});
    }
  }

  const isNameTooLong = name.length > GRID_VIEW_NAME_MAX_LENGTH;

  function setToggle(key: K) {
    return function onToggleChange(checked: boolean) {
      setValues((prev) => ({ ...prev, [key]: checked }));
      if (checked) setErrors((prev) => ({ ...prev, form: undefined }));
    };
  }

  async function handleConfirm() {
    const trimmed = name.trim();
    const next: typeof errors = {};
    if (!trimmed) next.name = "Name is required.";
    else if (existingNames.includes(trimmed)) next.name = "Duplicate name";
    if (requireOneOf && !requireOneOf.some((key) => values[key])) {
      next.form = "Pick at least one thing to save.";
    }
    setErrors(next);
    // The live char-limit warning is the message here; just block the save.
    if (isNameTooLong || Object.keys(next).length > 0) return false;

    try {
      const saved = Boolean(await onConfirm(trimmed, values));
      // Nothing saved and nothing thrown: still say so, rather than leave
      // the dialog open with no message.
      if (!saved) setErrors({ form: "Failed to save the view — try again." });
      return saved;
    } catch (error) {
      // The authoritative duplicate check — the client-side one above can be
      // stale (a view saved in another tab). Same inline red either way.
      if (isDuplicateGridViewNameError(error)) {
        setErrors({ name: "Duplicate name" });
      } else {
        setErrors({ form: "Failed to save the view — try again." });
      }
      return false;
    }
  }

  return (
    <Dialog
      // A name-and-toggles utility form — nothing to report.
      hideFeedbackIcon
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      confirmText="Save"
      cancelText="Cancel"
      onConfirm={handleConfirm}
      isLoading={isPending}
    >
      <div className={formContainer}>
        <div className={formField}>
          <Label label="Name" htmlFor="grid-view-name" />
          <InputField
            id="grid-view-name"
            placeholder={namePlaceholder}
            value={name}
            error={errors.name}
            // The name error stays until the next confirm: clearing it per
            // keystroke re-wraps the input (``FieldError``), which remounts
            // it and drops focus after the first character.
            onChange={(event) => setName(event.target.value)}
          />
          {isNameTooLong && (
            <span className={formErrorText}>
              {`Name must be ${GRID_VIEW_NAME_MAX_LENGTH} characters or fewer (currently ${name.length}).`}
            </span>
          )}
        </div>
        <div className={toggleList}>
          {toggles.map((toggle) => (
            <Switch
              key={toggle.key}
              checked={values[toggle.key]}
              label={toggle.label}
              onChange={setToggle(toggle.key)}
            />
          ))}
        </div>
        {errors.form && <span className={formErrorText}>{errors.form}</span>}
      </div>
    </Dialog>
  );
}
